import { keepPreviousData, useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { API_ENTRYPOINT_V2, TOKEN_KEY } from '@/utils/CONST';
import type { Avatar } from '@/utils/types';

/** Same embedding as place views so each place includes its site (`parent`). */
const VIEWPORT_SERIALIZATION_GROUPS = ['read_avatar_root', 'read_parent', 'read_avatar_parent'] as const;

export interface ViewportItem {
    id: string;
    lat: number;
    lng: number;
    /** Site (parent) when API embeds it via serialization groups. */
    parent?: string | Avatar;
}

export interface ViewportCluster {
    cell: string;
    count: number;
    lat: number;
    lng: number;
}

export interface ViewportItemsResponse {
    type: 'items';
    precision: number;
    data: ViewportItem[];
}

export interface ViewportClustersResponse {
    type: 'clusters';
    precision: number;
    data: ViewportCluster[];
}

export type ViewportResponse = ViewportItemsResponse | ViewportClustersResponse;

interface Params {
    bounds: {
        north: number;
        south: number;
        east: number;
        west: number;
    } | null;
    zoom: number;
    enabled?: boolean;
}

function useGetAvatarsViewport({ bounds, zoom, enabled = true }: Params) {
    const { data, isLoading, isFetching, error, refetch } = useQuery<ViewportResponse>({
        queryKey: ['avatars-viewport', bounds, zoom, [...VIEWPORT_SERIALIZATION_GROUPS]],
        queryFn: async () => {
            if (!bounds) {
                throw new Error('Bounds are required');
            }

            const token = localStorage.getItem(TOKEN_KEY);
            
            const params = new URLSearchParams({
                north: String(bounds.north),
                south: String(bounds.south),
                east: String(bounds.east),
                west: String(bounds.west),
                zoom: String(zoom),
            });
            VIEWPORT_SERIALIZATION_GROUPS.forEach((group, index) => {
                params.set(`serialization_groups[${index}]`, group);
            });

            const response = await axios.get<ViewportResponse>(
                `${API_ENTRYPOINT_V2}/api/avatars/viewport?${params.toString()}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            return response.data;
        },
        enabled: enabled && !!bounds,
        staleTime: 30 * 1000,
        placeholderData: keepPreviousData,
    });

    return {
        data,
        isLoading,
        isFetching,
        error,
        refetch,
    };
}

export default useGetAvatarsViewport;
